import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Typography, Spin, Empty, Tooltip } from 'antd';
import Table from 'components/Common/Table';
import dayjs from 'dayjs';
import * as actions from 'store/actions/AuthActions';
import './EmployeeDailyUpdates.css';

const { Text } = Typography;

const EmployeeDailyUpdates = ({ employeeId }) => {
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const fetchUpdates = useCallback(async (page = 1, pageSize = 10) => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const response = await actions.loadDailyUpdates(page, pageSize, { employee: employeeId });
            if (response) {
                setUpdates(response.results || []);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: response.count || 0,
                });
            }
        } catch (error) {
            console.error('Error fetching daily updates:', error);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        fetchUpdates();
    }, [fetchUpdates]);

    const handleTableChange = (newPagination) => {
        // A new page size starts again from page 1.
        const sizeChanged = newPagination.pageSize !== pagination.pageSize;
        fetchUpdates(sizeChanged ? 1 : newPagination.current, newPagination.pageSize);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'update_date',
            key: 'update_date',
            width: 120,
            render: (date) => dayjs(date).format('MMM DD, YYYY'),
            sorter: (a, b) => dayjs(a.update_date).unix() - dayjs(b.update_date).unix(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) =>
                status === 'SUBMITTED'
                    ? <Tag color="green">Submitted</Tag>
                    : <Tag color="orange">Draft</Tag>,
        },
        {
            title: 'Tasks Completed',
            dataIndex: 'tasks_completed',
            key: 'tasks_completed',
            ellipsis: true,
            render: (html) => {
                const plainText = html?.replace(/<[^>]*>/g, '') || '';
                return (
                    <Tooltip title={plainText.substring(0, 200)}>
                        <span>
                            {plainText.substring(0, 100)}
                            {plainText.length > 100 ? '...' : ''}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Challenges',
            dataIndex: 'challenges',
            key: 'challenges',
            width: 200,
            ellipsis: true,
            render: (html) => {
                const plainText = html?.replace(/<[^>]*>/g, '') || '';
                return plainText || <Text type="secondary">-</Text>;
            },
        },
    ];

    const expandedRowRender = (record) => (
        <div style={{ padding: '12px 0' }}>
            {record.tasks_completed && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: '#1890ff' }}>Tasks Completed</Text>
                    <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: record.tasks_completed }} />
                </div>
            )}
            {record.challenges && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: '#faad14' }}>Challenges</Text>
                    <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: record.challenges }} />
                </div>
            )}
            {record.plan_for_tomorrow && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: '#52c41a' }}>Plan for Tomorrow</Text>
                    <div style={{ marginTop: 4 }} dangerouslySetInnerHTML={{ __html: record.plan_for_tomorrow }} />
                </div>
            )}
        </div>
    );

    const handleExpand = (expanded, record) => {
        setExpandedRowKeys((prev) =>
            expanded
                ? [...prev, record.id]
                : prev.filter((key) => key !== record.id)
        );
    };

    if (!employeeId) {
        return <Empty description="No employee selected" />;
    }

    return (
        <div className="employee-daily-updates-scrollable" style={{ padding: '16px' }}>
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={updates}
                    rowKey="id"
                    expandable={{
                        expandedRowRender,
                        rowExpandable: () => true,
                        expandedRowKeys,
                        onExpand: handleExpand,
                    }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} updates`,
                    }}
                    onChange={handleTableChange}
                    size="middle"
                    scroll={{ x: 800 }}
                    locale={{ emptyText: <Empty description="No daily updates found" /> }}
                />
            </Spin>
        </div>
    );
};

export default EmployeeDailyUpdates;
